import { useMemo, useState } from 'react';
import { T, useGT } from 'gt-react';
import { toast } from 'sonner';
import type { SendAdHocNotificationOutput } from '@erp/rpc';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Textarea } from 'src/components/ui/textarea';
import { useComposeReplySendableMailboxes } from 'src/api/rpc/hooks/communications/useSendComposeReply';
import { useSendAdHocNotification } from '@/api/rpc/hooks/communications/useNotifications';
import {
  NotifyRecipientsFields,
  type NotifyRecipientsValue,
} from 'src/pages/authenticated/workflows/components/config-renderers/shared/NotifyRecipientsFields';
import { NotifyMessageTemplateSelect } from './NotifyMessageTemplateSelect';
import {
  CUSTOM_MESSAGE_VALUE,
  useDefaultNotifyMessageValue,
} from './notifyMessageTemplates';
import {
  buildSendNotificationInput,
  computeNotifyBlockers,
  emailCategoryForSubject,
  messageCategoryForSubject,
  templateRefOf,
  type NotifyBlocker,
  type SendNotificationSubject,
} from './sendNotificationForm';

export type { SendNotificationSubject } from './sendNotificationForm';

/**
 * Pre-filled values (the AI assistant's `open_notify_dialog`). Applied as the
 * dialog's initial state — remount with a `key` to apply a new set.
 */
export interface SendNotificationDefaults {
  recipientUserIds?: string[];
  recipientUserGroupIds?: string[];
  recipientContactIds?: string[];
  channels?: NotifyRecipientsValue['channels'];
  /** Template ref/id to preselect; when absent with a title or body, Custom message is chosen. */
  templateId?: string;
  title?: string;
  body?: string;
}

function initialFields(defaults: SendNotificationDefaults | undefined) {
  return {
    ...EMPTY_VALUE,
    ...(defaults?.recipientUserIds && {
      recipientUserIds: defaults.recipientUserIds,
    }),
    ...(defaults?.recipientUserGroupIds && {
      recipientUserGroupIds: defaults.recipientUserGroupIds,
    }),
    ...(defaults?.recipientContactIds && {
      recipientContactIds: defaults.recipientContactIds,
    }),
    ...(defaults?.channels && { channels: defaults.channels }),
  };
}

function initialMessageChoice(
  defaults: SendNotificationDefaults | undefined,
): string | null {
  if (defaults?.templateId) return defaults.templateId;
  if (defaults?.title || defaults?.body) return CUSTOM_MESSAGE_VALUE;
  return null;
}

const EMPTY_VALUE: NotifyRecipientsValue = {
  recipientUserIds: [],
  recipientUserGroupIds: [],
  recipientContactIds: [],
  channels: ['in_app', 'email'],
  senderMailbox: '',
  emailTemplateId: '',
  emailSubject: '',
};

/** Result toast: a zero-recipient send is a warning, not a success. */
function toastSendResult(
  res: SendAdHocNotificationOutput,
  gt: ReturnType<typeof useGT>,
) {
  if (res.recipientCount === 0) {
    toast.warning(
      gt('No recipients could be resolved — nothing was sent.', {
        context: 'notify dialog result toast',
      }),
    );
    return;
  }
  toast.success(
    gt('Notification sent to {count} recipient(s)', {
      context: 'notify dialog result toast',
      count: String(res.recipientCount),
    }),
  );
}

/** Free-form title + message, shown only for the "Custom message" option. */
function CustomMessageFields({
  title,
  body,
  onTitleChange,
  onBodyChange,
}: {
  title: string;
  body: string;
  onTitleChange: (next: string) => void;
  onBodyChange: (next: string) => void;
}) {
  const gt = useGT();
  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="adhoc-notify-title" className="text-sm font-medium">
          <T context="notify dialog field">Title</T>{' '}
          <span className="text-destructive">*</span>
        </Label>
        <Input
          id="adhoc-notify-title"
          value={title}
          maxLength={200}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder={gt('Short headline', {
            context: 'notify title placeholder',
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="adhoc-notify-body" className="text-sm font-medium">
          <T context="notify dialog field">Custom message</T>{' '}
          <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="adhoc-notify-body"
          value={body}
          rows={3}
          maxLength={4000}
          onChange={(e) => onBodyChange(e.target.value)}
          placeholder={gt('What the recipients should know', {
            context: 'notify body placeholder',
          })}
        />
      </div>
    </>
  );
}

/**
 * Admin one-off "Notify…" — the Notify workflow step's recipients / channels
 * fields, fired once about the given subject (an order or a discount group)
 * and persisted only as the resulting notification rows. The message is
 * either a notification template (grouped Sales orders / Discount requests;
 * the template then supplies the in-app title/body and the email) or a
 * custom free-form title + message. The sender mailbox prepopulates from the
 * org default for the subject's email category.
 */
export function SendNotificationDialog({
  open,
  onOpenChange,
  subject,
  defaults,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subject?: SendNotificationSubject;
  defaults?: SendNotificationDefaults;
}) {
  const gt = useGT();
  const [fields, setFields] = useState<NotifyRecipientsValue>(() =>
    initialFields(defaults),
  );
  const [title, setTitle] = useState(defaults?.title ?? '');
  const [body, setBody] = useState(defaults?.body ?? '');
  // null = untouched, so the default can follow the (async) template list
  // without an effect; once the user picks, their choice wins.
  const [chosenMessage, setChosenMessage] = useState<string | null>(() =>
    initialMessageChoice(defaults),
  );
  const [attempted, setAttempted] = useState(false);
  const send = useSendAdHocNotification();

  const category = emailCategoryForSubject(subject);
  const defaultMessage = useDefaultNotifyMessageValue(
    messageCategoryForSubject(subject),
  );
  const messageValue = chosenMessage ?? defaultMessage;
  const templateRef = templateRefOf(messageValue);
  const { data: sendable } = useComposeReplySendableMailboxes(category);
  const mailboxOptions = useMemo(
    () =>
      (sendable?.mailboxes ?? []).map((m) => ({
        upn: m.upn,
        label: m.name ? `${m.name} <${m.upn}>` : m.upn,
      })),
    [sendable],
  );
  // Derive the sender from the category default until the user picks one —
  // no effect needed, and the org default is never persisted anywhere.
  const senderMailbox = fields.senderMailbox || (sendable?.defaultUpn ?? '');
  // The message picker owns the template; mirror it so the email section's
  // custom-subject field follows the same no-template convention as the step.
  const value: NotifyRecipientsValue = {
    ...fields,
    senderMailbox,
    emailTemplateId: templateRef ?? '',
  };

  const formState = { value, messageValue, title, body };
  const blockerCopy: Record<NotifyBlocker, string> = {
    no_recipients: gt('Pick at least one user, group, or contact.', {
      context: 'notify dialog validation',
    }),
    contacts_need_email: gt(
      'Address-book contacts only receive email — enable the email channel.',
      { context: 'notify dialog validation' },
    ),
    no_sender_mailbox: gt('Pick a sender mailbox.', {
      context: 'notify dialog validation',
    }),
    no_title: gt('A title is required.', {
      context: 'notify dialog validation',
    }),
    no_body: gt('A message is required.', {
      context: 'notify dialog validation',
    }),
  };
  const blockers = computeNotifyBlockers(formState).map(
    (key) => blockerCopy[key],
  );

  // Typing a custom message pins the picker to Custom, so a template list
  // that finishes loading afterwards can't swap the default underneath it.
  function editCustom(set: (next: string) => void) {
    return (next: string) => {
      setChosenMessage(CUSTOM_MESSAGE_VALUE);
      set(next);
    };
  }

  function reset() {
    setFields(initialFields(defaults));
    setTitle(defaults?.title ?? '');
    setBody(defaults?.body ?? '');
    setChosenMessage(initialMessageChoice(defaults));
    setAttempted(false);
  }

  function close(next: boolean) {
    if (!next && send.isPending) return;
    if (!next) reset();
    onOpenChange(next);
  }

  function submit() {
    setAttempted(true);
    if (blockers.length > 0) return;
    send.mutate(buildSendNotificationInput(formState, subject), {
      onSuccess: (res) => {
        toastSendResult(res, gt);
        reset();
        onOpenChange(false);
      },
      onError: (err) =>
        toast.error(
          gt('Could not send the notification: {message}', {
            context: 'ad-hoc notification error toast',
            message: err instanceof Error ? err.message : '',
          }),
        ),
    });
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            <T context="admin dialog title, send a one-off notification">
              Notify people
            </T>
          </DialogTitle>
          <DialogDescription>
            {subject ? (
              <T context="notify dialog description when a subject is set">
                Send a one-off in-app and/or email notification about this
                record. Recipients only receive it if they can see the record.
                Pick a template, or choose Custom message and use{' '}
                {'{{customerName}}'} in the title or message to insert the
                customer.
              </T>
            ) : (
              <T context="notify dialog description without a subject">
                Send a one-off in-app and/or email notification to the selected
                people. Pick a template or write a custom message.
              </T>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label
              htmlFor="adhoc-notify-message"
              className="text-sm font-medium"
            >
              <T context="notify dialog field: template or custom message picker">
                Message
              </T>
            </Label>
            <NotifyMessageTemplateSelect
              id="adhoc-notify-message"
              value={messageValue}
              onChange={setChosenMessage}
            />
            {templateRef ? (
              <p className="text-[11px] text-muted-foreground">
                <T context="help text when a notify template is selected">
                  The template provides the title, message, and email body for
                  this record — the same as the automatic notifications.
                </T>
              </p>
            ) : null}
          </div>

          {!templateRef && (
            <CustomMessageFields
              title={title}
              body={body}
              onTitleChange={editCustom(setTitle)}
              onBodyChange={editCustom(setBody)}
            />
          )}

          <NotifyRecipientsFields
            value={value}
            onChange={(patch) => setFields((prev) => ({ ...prev, ...patch }))}
            mailboxOptions={mailboxOptions}
            hideEmailTemplate
          />

          {attempted && blockers.length > 0 ? (
            <ul className="list-disc space-y-0.5 pl-4 text-xs text-destructive">
              {blockers.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => close(false)}
            disabled={send.isPending}
          >
            <T context="cancel button">Cancel</T>
          </Button>
          <Button onClick={submit} disabled={send.isPending}>
            {send.isPending ? (
              <T context="notify dialog submit, in progress">Sending…</T>
            ) : (
              <T context="notify dialog submit button">Send notification</T>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
